import { useEffect, useRef, useState } from "react";
import { Send, UserCheck, Bot, RefreshCw, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const AGENTS = [
    { id: "tanya", name: "Tanya" },
    { id: "jyoti", name: "Jyoti" },
];

export default function AdminConciergeInbox() {
    const [sessions, setSessions] = useState([]);
    const [activeSid, setActiveSid] = useState(null);
    const [active, setActive] = useState(null);
    const [draft, setDraft] = useState("");
    const [agent, setAgent] = useState(AGENTS[0].name);
    const [loading, setLoading] = useState(false);
    const pollRef = useRef(null);
    const scrollRef = useRef(null);

    const loadList = async () => {
        try {
            const r = await api.get("/admin/concierge/sessions");
            setSessions(r.data);
            if (!activeSid && r.data.length) {
                setActiveSid(r.data[0].session_id);
            }
        } catch { /* silent */ }
    };

    const loadActive = async (sid) => {
        if (!sid) return;
        try {
            const r = await api.get(`/admin/concierge/sessions/${sid}`);
            setActive(r.data);
        } catch {
            toast.error("Failed to load session");
        }
    };

    useEffect(() => { loadList(); }, []);

    useEffect(() => {
        if (!activeSid) return;
        loadActive(activeSid);
        clearInterval(pollRef.current);
        pollRef.current = setInterval(() => loadActive(activeSid), 4000);
        return () => clearInterval(pollRef.current);
    }, [activeSid]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [active?.messages?.length]);

    const takeOver = async () => {
        if (!activeSid) return;
        setLoading(true);
        try {
            await api.post(`/admin/concierge/sessions/${activeSid}/take-over`, { agent_name: agent });
            toast.success(`${agent} is now live in this chat`);
            await loadActive(activeSid);
        } catch { toast.error("Failed to take over"); }
        finally { setLoading(false); }
    };

    const release = async () => {
        if (!activeSid) return;
        setLoading(true);
        try {
            await api.post(`/admin/concierge/sessions/${activeSid}/release`);
            toast.success("Released back to AI");
            await loadActive(activeSid);
        } catch { toast.error("Failed to release"); }
        finally { setLoading(false); }
    };

    const sendReply = async () => {
        const text = draft.trim();
        if (!text || !activeSid) return;
        setLoading(true);
        try {
            await api.post(`/admin/concierge/sessions/${activeSid}/send`, { agent_name: agent, content: text });
            setDraft("");
            await loadActive(activeSid);
        } catch { toast.error("Failed to send"); }
        finally { setLoading(false); }
    };

    return (
        <div data-testid="admin-concierge-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-serif text-[28px]">AI Concierge Inbox ({sessions.length})</h2>
                <button onClick={loadList} className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E7EB] hover:border-[#003E4D]" data-testid="concierge-refresh">
                    <RefreshCw size={12} /> Refresh
                </button>
            </div>

            <div className="grid grid-cols-12 gap-4 mt-6 min-h-[520px]">
                {/* Sessions list */}
                <div className="col-span-12 md:col-span-4 border border-[#E5E7EB] rounded-xl overflow-hidden">
                    <div className="px-4 py-2.5 bg-stone-50 border-b border-[#E5E7EB] text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF]">Sessions</div>
                    <div className="max-h-[560px] overflow-y-auto">
                        {sessions.length === 0 && <p className="p-4 text-[13px] text-[#9CA3AF]">No chat sessions yet.</p>}
                        {sessions.map((s) => {
                            const last = s.messages?.[s.messages.length - 1];
                            const isActive = activeSid === s.session_id;
                            return (
                                <button
                                    key={s.session_id}
                                    onClick={() => setActiveSid(s.session_id)}
                                    className={`w-full text-left px-4 py-3 border-b border-[#E5E7EB] hover:bg-stone-50 ${isActive ? "bg-[#003E4D]/5" : ""}`}
                                    data-testid={`concierge-session-${s.session_id}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="text-[13px] font-semibold">{s.name || "Anonymous"} {s.phone && <span className="text-[11px] text-[#003E4D] font-normal">· {s.phone}</span>}</div>
                                        {s.human_taken_over && <span className="text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">LIVE</span>}
                                        {!s.human_taken_over && s.handoff_requested && <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">WANTS HUMAN</span>}
                                    </div>
                                    <div className="text-[11px] text-[#9CA3AF] truncate mt-1">{last?.content || "—"}</div>
                                    <div className="text-[10px] text-[#9CA3AF] mt-0.5">{new Date(s.started_at).toLocaleString()}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Active conversation */}
                <div className="col-span-12 md:col-span-8 border border-[#E5E7EB] rounded-xl overflow-hidden flex flex-col">
                    {!active && <div className="p-8 text-center text-[#9CA3AF]">Select a session to view conversation</div>}
                    {active && (
                        <>
                            <div className="px-4 py-3 bg-stone-50 border-b border-[#E5E7EB] flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <div className="text-[14px] font-semibold">{active.name || "Anonymous visitor"}</div>
                                    <div className="text-[11px] text-[#4a5568] flex flex-wrap items-center gap-3 mt-0.5">
                                        {active.phone && <span className="inline-flex items-center gap-1"><Phone size={11}/>{active.phone}</span>}
                                        {active.city && <span className="inline-flex items-center gap-1"><MapPin size={11}/>{active.city}</span>}
                                        <span>Entry: <code className="bg-stone-200/60 px-1 rounded">{active.entry_page}</code></span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <select value={agent} onChange={(e) => setAgent(e.target.value)} className="text-[12px] border border-[#E5E7EB] rounded px-2 py-1.5" data-testid="agent-select">
                                        {AGENTS.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
                                    </select>
                                    {!active.human_taken_over ? (
                                        <button onClick={takeOver} disabled={loading} className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#003E4D] text-white" data-testid="concierge-take-over">
                                            <UserCheck size={12}/> Take over
                                        </button>
                                    ) : (
                                        <button onClick={release} disabled={loading} className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#003E4D] text-[#003E4D]" data-testid="concierge-release">
                                            <Bot size={12}/> Release to AI
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-stone-50/40 max-h-[460px]">
                                {(active.messages || []).map((m, i) => (
                                    <ChatRow key={i} role={m.role} content={m.content} ts={m.created_at} agent_name={active.agent_name} />
                                ))}
                            </div>

                            {active.human_taken_over && (
                                <div className="p-3 border-t border-[#E5E7EB] bg-white">
                                    <div className="flex items-end gap-2">
                                        <textarea
                                            value={draft}
                                            onChange={(e) => setDraft(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                                            rows={2}
                                            placeholder={`Reply as ${agent}…`}
                                            className="flex-1 resize-none border border-[#E5E7EB] rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-[#003E4D]"
                                            data-testid="concierge-agent-input"
                                        />
                                        <button onClick={sendReply} disabled={!draft.trim() || loading} className="px-4 py-2 rounded-lg bg-[#003E4D] text-white disabled:opacity-40" data-testid="concierge-agent-send">
                                            <Send size={16}/>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function ChatRow({ role, content, ts, agent_name }) {
    const isUser = role === "user";
    const isAgent = role === "agent";
    const isBot = role === "assistant";
    const align = isUser ? "justify-end" : "justify-start";
    const bg = isUser
        ? "bg-[#003E4D] text-white"
        : isAgent
            ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
            : "bg-white text-stone-800 border border-stone-200";
    const label = isUser ? "Visitor" : isAgent ? (agent_name || "Agent") : isBot ? "Arushi (AI)" : role;
    return (
        <div className={`flex ${align}`}>
            <div className={`${bg} rounded-2xl px-3.5 py-2.5 text-[13.5px] max-w-[78%] shadow-sm`}>
                <div className="text-[10px] font-semibold opacity-70 mb-0.5">{label}</div>
                <div className="whitespace-pre-wrap leading-relaxed">{content}</div>
                <div className="text-[9px] opacity-60 mt-1">{ts ? new Date(ts).toLocaleTimeString() : ""}</div>
            </div>
        </div>
    );
}
